import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

/**
 * Replaces `next-themes`.
 *
 * Only three files import the library, and they use just `{ theme, setTheme }`
 * plus the provider itself. The real reason for the shim is that
 * `next-themes`' provider renders an inline `<script>` from React, which React 19
 * refuses to execute (it warns "scripts inside React components are never
 * executed"). The script exists to apply the stored theme before first paint, so
 * the equivalent snippet now lives in `index.html` — where a Vite SPA can run it
 * for real — and this provider only handles the state.
 *
 * Props mirror the ones `AppProviders` passed to `next-themes`.
 */

const STORAGE_KEY = "theme";
const MEDIA_QUERY = "(prefers-color-scheme: dark)";

export type Theme = "light" | "dark" | "system";

type ThemeContextValue = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  resolvedTheme: "light" | "dark";
  themes: Theme[];
  systemTheme: "light" | "dark";
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

function systemTheme(): "light" | "dark" {
  if (typeof window === "undefined") return "light";
  return window.matchMedia(MEDIA_QUERY).matches ? "dark" : "light";
}

function storedTheme(fallback: Theme): Theme {
  if (typeof window === "undefined") return fallback;
  try {
    const value = window.localStorage.getItem(STORAGE_KEY) as Theme | null;
    return value ?? fallback;
  } catch {
    return fallback;
  }
}

export function ThemeProvider({
  children,
  attribute = "class",
  defaultTheme = "system",
  enableSystem = true,
  disableTransitionOnChange = false,
}: {
  children: ReactNode;
  attribute?: string | string[];
  defaultTheme?: Theme;
  enableSystem?: boolean;
  disableTransitionOnChange?: boolean;
}) {
  const [theme, setThemeState] = useState<Theme>(() => storedTheme(defaultTheme));
  const [resolved, setResolved] = useState<"light" | "dark">(() =>
    theme === "system" ? systemTheme() : theme,
  );

  const resolvedTheme: "light" | "dark" = theme === "system" ? resolved : theme;

  const apply = useCallback(
    (next: Theme) => {
      const value = next === "system" ? resolved : next;
      const root = document.documentElement;

      const paint = (t: "light" | "dark") => {
        if (attribute === "class") {
          root.classList.remove("light", "dark");
          root.classList.add(t);
        } else {
          const attributes = Array.isArray(attribute) ? attribute : [attribute];
          for (const name of attributes) {
            if (name === "class") continue;
            if (name.startsWith("data-")) root.setAttribute(name, t);
            else root.removeAttribute(name);
          }
        }
        // next-themes does the same via `enableColorScheme`.
        root.style.colorScheme = t;
      };

      if (disableTransitionOnChange) {
        // Suppress transitions while the class changes, exactly like the
        // style element next-themes injects for this flag.
        const style = document.createElement("style");
        style.appendChild(
          document.createTextNode(
            "*,*::before,*::after{-webkit-transition:none!important;-moz-transition:none!important;-o-transition:none!important;-ms-transition:none!important;transition:none!important}",
          ),
        );
        document.head.appendChild(style);
        paint(value);
        // Two frames: one to flush the recalculation, one to drop the override.
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            document.head.removeChild(style);
          }),
        );
        return;
      }

      paint(value);
    },
    [attribute, disableTransitionOnChange, resolved],
  );

  useEffect(() => {
    if (!enableSystem) return;
    const media = window.matchMedia(MEDIA_QUERY);
    const onChange = () => {
      setResolved(media.matches ? "dark" : "light");
    };
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [enableSystem]);

  useEffect(() => {
    apply(theme);
  }, [apply, theme]);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY) setThemeState(storedTheme(defaultTheme));
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [defaultTheme]);

  const setTheme = useCallback(
    (next: Theme) => {
      setThemeState(next);
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // Private mode: the theme still applies for this session.
      }
    },
    [],
  );

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      setTheme,
      resolvedTheme,
      themes: enableSystem ? ["light", "dark", "system"] : ["light", "dark"],
      systemTheme: resolved,
    }),
    [theme, setTheme, resolvedTheme, resolved, enableSystem],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used inside a <ThemeProvider>.");
  }
  return context;
}